import { Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect } from 'react';

import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { ButtonMoreStyled } from './cv-video-row.styled';
import CVVideo from '../../models/cv-video';
import CvVideoStatus from '@cv-videos/models/cv-video-status';
import { CvVideoStatusChip } from '@atoms/CvVideoStatusChip';
import { IconSize } from '@constants';
import JTableCell from '@atoms/JTabelCell';
import Paths from '@/routes/paths';
import { TableRowStyled } from '@styled/table-row.styled';
import { formatDate } from '@lib/dateUtils';
import { useEditCVVideoByIdMutation } from '@cv-videos/mutations/cvVideoMutations';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  row: CVVideo;
  single?: boolean;
  filterStatus?: string;
}

const CvVideoRow = ({ row, filterStatus, single = false }: Props) => {
  const { t } = useTranslation();
  const { candidate, createdAt, id, name, status } = row;
  const { mutate: mutateCvVideo, isSuccess: isMutateVideoSuccess } =
    useEditCVVideoByIdMutation();

  const theme = useTheme();
  const navigation = useNavigate();

  const navigateToDetails = useCallback(() => {
    navigation(
      `${Paths.CvVideoDetails.replace(':id', id)}${
        filterStatus ? `#${filterStatus}` : ''
      }`,
    );
  }, [navigation, filterStatus]);

  const onNavigateClick = useCallback(() => {
    if (status === CvVideoStatus.PENDING) {
      mutateCvVideo({
        id,
        payload: {
          status: CvVideoStatus.PROCESSING,
        },
      });
    } else {
      navigateToDetails();
    }
  }, [status]);

  useEffect(() => {
    if (!isMutateVideoSuccess) return;

    navigateToDetails();
  }, [isMutateVideoSuccess]);

  return (
    <TableRowStyled key={id} data-test={`candidate-list-row-${id}`}>
      <JTableCell isbold>{candidate?.user?.firstname}</JTableCell>
      <JTableCell isbold>{candidate?.user?.lastname}</JTableCell>
      <JTableCell>{candidate && formatDate(candidate?.createdAt)}</JTableCell>
      <JTableCell>
        {candidate?.user &&
          (candidate.user.address.cityName ??
            `${candidate.user.address.city.name} ${candidate.user.address.city.zipCode}`)}
      </JTableCell>

      <JTableCell>{name}</JTableCell>
      <JTableCell>
        <Typography>{formatDate(createdAt)}</Typography>
      </JTableCell>
      <JTableCell>
        <Stack gap={1}>
          <CvVideoStatusChip
            label={t(`${status.toUpperCase()}_CV`)}
            status={status}
          ></CvVideoStatusChip>
        </Stack>
      </JTableCell>
      {!single && (
        <JTableCell>
          <ButtonMoreStyled
            data-qa="navigate-to-detail"
            onClick={onNavigateClick}
          >
            <ArrowRightIcon
              height={IconSize.buttonIcon}
              color={theme.palette.white.main}
            />
          </ButtonMoreStyled>
        </JTableCell>
      )}
    </TableRowStyled>
  );
};

export default CvVideoRow;
